import { ZodArray, ZodDefault, ZodEffects, ZodNullable, ZodObject, ZodOptional, ZodTuple, type ZodTypeAny } from 'zod';

import { getComponentValidator } from './get-component-validator';
import type { ComponentType } from './validators/fields/ComponentType';

/**
 * The fields a component TYPE declares at a schema path. Lets the datasheet decide field
 * visibility from the definition rather than from whatever a stored document happens to contain.
 */
export const getComponentFieldsAtPath = (type: ComponentType, path: string): Record<string, unknown> => {
    let schema: ZodTypeAny | null = getComponentValidator(type);

    for (const key of path.split('.')) {
        schema = schema && descend(schema, key);
    }

    return schema ? getSchemaFields(schema) : {};
};

export const getComponentSectionFields = (type: ComponentType, section: string): Record<string, unknown> =>
    getComponentFieldsAtPath(type, section);

export const getSchemaFields = (schema: ZodTypeAny): Record<string, unknown> => {
    const object = unwrap(schema);

    return object instanceof ZodObject ? object.shape : {};
};

const descend = (schema: ZodTypeAny, key: string): ZodTypeAny | null => {
    const inner = unwrap(schema);

    if (inner instanceof ZodObject) return inner.shape[key] ?? null;

    const index = Number(key);
    if (!Number.isInteger(index) || index < 0) return null;

    if (inner instanceof ZodArray) return inner._def.type;
    if (inner instanceof ZodTuple) return inner._def.items[index] ?? null;

    return null;
};

const unwrap = (schema: ZodTypeAny): ZodTypeAny => {
    let current = schema;

    while (true) {
        if (current instanceof ZodEffects) current = current._def.schema;
        else if (current instanceof ZodDefault) current = current._def.innerType;
        else if (current instanceof ZodOptional) current = current._def.innerType;
        else if (current instanceof ZodNullable) current = current._def.innerType;
        else return current;
    }
};
